import PositiveNegativeButton from "../CommonButtons/PositiveNegativeButton";
import SVGComponent from "../SVGComponent";

import React, { useState } from "react";
import ICONS from "../../../assets/Icons/index.js";
import { standardAccountNumberFormat } from "../../../utils/standardAccountNumberFormat.js";
import Loader from "../../Loader/Index.jsx";
import CommonAlertsStrip from "../CommonAlertsStrip/index.jsx";
import CommonBankLogo from "../CommonBankLogo/index.jsx";
import PrimaryButton from "../CommonButtons/PrimaryButton";
import SecondaryRightIconSmallButton from "../CommonButtons/SecondaryRightIconSmallButton/index.jsx";
import FormatAmount from "../FormatAmount/FormatAmount.js";
import TitleSubtitlePair from "../TitleSubtitlePair/index.jsx";
/**
 * @typedef {((e: React.MouseEvent<HTMLButtonElement, MouseEvent>?) => void)} OnClickEventHandler
 */
/**
 * @typedef {("textSubtext" | "textSubtextX2" | "accountNumber" | "amount" | "cta" | "ctaX2")} EntryType
 * @typedef {("button" | "submit")} BtnType
 * @typedef {Object} Entry
 * @property {String} text Text to display
 * @property {String} [textColor]
 * @property {String} subText Subtext to display
 * @property {String} [subText1] Second subtext to display
 * @property {String} [text1] Second text to display
 * @property {String} [text1Color]
 * @property {String} [icon] Icon to display
 * @property {String} [iconColor] Colour of icon
 * @property {OnClickEventHandler} [onClick] What to do on click
 * @property {OnClickEventHandler} [onClick1] What to do on click (right side)
 * @property {String} [ifsc] Ifsc number to generate bank logo
 * @property {EntryType} type Type of this entry, one of ("textSubtext" | "textSubtextX2" | "accountNumber" | "amount" | "cta")
 * @property {boolean} [isBold] Is text bold
 * @property {boolean} [isBoldR] Is right text bold
 * @property {OnClickEventHandler} refreshOnClick what to do when click on refresh button
 * @property {boolean} shouldShowRefreshButton should the refresh button be shown
 *
 * @typedef {Object} Alert1
 * @property {String} text Text to show
 * @property {String} [icon] Icon to show, defaults to alert icon
 * @property {String} iconColor Icon colour
 * @property {boolean} isLoading Is this alert loading
 * @property {OnClickEventHandler} onClick What to do on click
 *
 *
 * @param {Object} props
 * @param {Entry[][]} props.entries List of list of `Entry`, 3 items in the outer list corresponds to the 3 columns in this card.
 * @param {OnClickEventHandler} props.approveClick What to do on approve button click
 * @param {OnClickEventHandler} props.rejectClick What to do on reject button click
 * @param {String} props.ctaBtn1Text  Top right cta button text
 * @param {OnClickEventHandler} props.ctaBtn1OnClick Top right cta button on click
 * @param {String} props.ctaBtn2Text top right cta button 2  text
 * @param {OnClickEventHandler} props.ctaBtn2OnClick top right cta button 2 on click
 * @param {("default" | "single" | "double")} props.cardState if this is "default" then approve reject button is shown, if this is "single" then account approval pending screen is shown, if this is "double" reject reason screen is shown
 * @param {String} props.overlay1Text Text on overlay 1 (account approval pending screen)
 * @param {String} [props.overlay1TextColor] Text color on overlay 1
 * @param {String} props.overlay1Subtext subtext on overlay 1 (account approval pending screen)
 * @param {String} [props.overlay1SubtextColor] subtext color on overlay 1
 * @param {String} [props.overlay1Icon] Icon on overlay 1 (account approval pending screen)
 * @param {String} [props.overlay1IconColor] Icon color on overlay 1 (account approval pending screen)
 * @param {String} props.overlay1CtaText text on overlay 1 cta button (account approval pending screen)
 * @param {Boolean} props.overlay1CtaInactive is overlay 1 cta button inactive (account approval pending screen)
 * @param {Boolean} props.overlay1CtaDisabled is overlay 1 cta button disabled (account approval pending screen)
 * @param {String} props.overlay1CtaColor cta button background color
 * @param {OnClickEventHandler} props.overlay1CtaOnClick what to do on overlay 1 cta button click (account approval pending screen)
 * @param {boolean} props.overlay1CtaLoading is overlay 1 cta button loading (account approval pending screen)
 * @param {String} [props.overlay1Cta2Text] text on overlay 1 cta button 2 (account approval pending screen)
 * @param {Boolean} props.overlay1Cta2Inactive is overlay 1 cta button 2 inactive (account approval pending screen)
 * @param {("black" | "white")} [props.overlay1Cta2HoverType] overlay 1 cta 2 hover black or white,
 * @param {Boolean} props.overlay1Cta2Disabled is overlay 1 cta button 2 disabled (account approval pending screen)
 * @param {OnClickEventHandler} props.overlay1Cta2OnClick what to do on overlay 1 cta button 2 click (private view all)
 * @param {boolean} props.overlay1Cta2Loading is overlay 1 cta button loading (account approval pending screen)
 * @param {String} props.overlay1Cta2Color cta button background color
 * @param {String} props.overlay2Text overlay 2 text (reject reason screen)
 * @param {String} props.overlay2Subtext overlay 2 subtext (reject reason screen)
 * @param {import("../CommonAlertsStrip/index.jsx").Alert[]} props.alerts list of Alerts.
 * @param {String} props.approveBtnText
 * @param {String} props.rejectBtnText
 *
 * @param {boolean} props.overrideAutoLoader Take manual control of approve / reject loading animation
 * @param {("none" | "approve" | "reject")} props.whichBtnClicked - Whether approve or reject or none button is clicked
 * influcences what color loader is shown, only useful is overrideAutoLoader is true
 * @returns {JSX.Element}
 */
export default function CommonAccountApprovalCard({
  entries,
  approveClick,
  rejectClick,
  ctaBtn1Text,
  ctaBtn1OnClick,
  ctaBtn2Text,
  ctaBtn2OnClick,
  cardState = "default",
  overlay1Text,
  overlay1TextColor,
  overlay1Subtext,
  overlay1SubtextColor,
  overlay1Icon,
  overlay1IconColor,
  overlay1CtaText,
  overlay1CtaOnClick,
  overlay1CtaDisabled,
  overlay1CtaColor,
  overlay1CtaInactive,
  overlay1CtaLoading,
  overlay1Cta2Text,
  overlay1Cta2OnClick,
  overlay1Cta2HoverType,
  overlay1Cta2Disabled,
  overlay1Cta2Inactive,
  overlay1Cta2Loading,
  overlay1Cta2Color,
  overlay2Text,
  overlay2Subtext,
  alerts,
  approveBtnText,
  rejectBtnText,
  overrideAutoLoader,
  whichBtnClicked,
}) {
  /**
   * @type {Entry[][]}
   */

  //   const entry = [[], [], []];
  /**
   *
   * @param {Entry} entry
   * @param {Entry[]} entries
   * @param {number} index
   * @param {boolean} halfWidth
   */

  function processEntry(entry, index, entries, halfWidth) {
    const ctaDisabled = clickedBtn !== "none";
    switch (entry.type) {
      case "cta":
        return (
          <div
            className={`z-z-index-content relative  ${halfWidth ? "flex-1 min-w-0" : "w-full"} `}
          >
            <TitleSubtitlePair
              enableTicker={true}
              hoverOption="white"
              showArrow={true}
              isDisabled={(entry.isDisabled ?? false) || ctaDisabled}
              onClick={ctaDisabled ? undefined : (entry?.onClick ?? undefined)}
              subtitle={entry.subText}
              variant="sm"
              title={entry.text}
              titleColor={entry.textColor ?? "text-primary"}
              isBold={entry.isBold ?? false}
            />
          </div>
        );
      case "ctaX2":
        return (
          <div
            className={`flex flex-row  items-center gap-gap-sm justify-between 
              ${halfWidth ? "flex-1 min-w-0" : "w-full"}   Qlg:w-approval-card-slot-width w-full 
              ${entry.onClick ? "" : "pl-padding-sm"} ${entry.onClick1 ? "" : "pr-padding-sm"}`}
          >
            <div className="w-width-md flex-shrink-0 flex-grow-0">
              <TitleSubtitlePair
                enableTicker
                title={entry.text}
                subtitle={entry.subText}
                isDisabled={(entry.isDisabled ?? false) || ctaDisabled}
                titleColor={entry.textColor ?? "text-primary"}
                variant="sm"
                isBold={entry.isBold ?? false}
                onClick={
                  ctaDisabled ? undefined : (entry?.onClick ?? undefined)
                }
                showArrow={entry.onClick}
                hoverOption="white"
              />
            </div>
            <div className="w-width-md flex-shrink-0 flex-grow-0">
              <TitleSubtitlePair
                alignRight
                textAlign="right"
                isDisabled={(entry.isDisabled ?? false) || ctaDisabled}
                enableTicker
                titleColor={entry.text1Color ?? "text-primary"}
                title={entry.text1 ?? "text 1"}
                subtitle={entry.subText1 ?? "subtext 1"}
                variant="sm"
                isBold={entry.isBoldR ?? false}
                onClick={
                  ctaDisabled ? undefined : (entry?.onClick1 ?? undefined)
                }
                showArrow={entry.onClick1}
                hoverOption="white"
              />
            </div>
          </div>
        );
      case "textSubtextX2":
        return (
          <div
            className={`flex flex-row  items-center gap-gap-sm justify-start 
              ${halfWidth ? "flex-1 min-w-0" : "w-full"} Qlg:px-padding-sm px-padding-sm 
              w-full
              ${(entries?.[index - 1]?.type ?? "") !== "cta" ? "pt-padding-sm" : ""}
              ${index === longestEntrySize - 1 ? "Qlg:pb-padding-sm pb-padding-base" : ""}
              ${index === 0 ? "Qlg:pt-padding-sm pt-padding-base" : ""}
              `}
          >
            <div className="w-width-md flex-shrink-0 flex-grow-0">
              <TitleSubtitlePair
                enableTicker
                title={entry.text}
                subtitle={entry.subText}
                titleColor={entry.textColor ?? "text-primary"}
                variant="sm"
                isBold={entry.isBold ?? false}
              />
            </div>
            <div className="w-width-md  flex-grow-0">
              <TitleSubtitlePair
                alignRight
                textAlign="right"
                enableTicker
                titleColor={entry.text1Color ?? "text-primary"}
                title={entry.text1 ?? "text 1"}
                subtitle={entry.subText1 ?? "subtext 1"}
                variant="sm"
                isBold={entry.isBoldR ?? false}
              />
            </div>
          </div>
        );
      case "accountNumber":
        return (
          <div
            className={`flex flex-row justify-between items-center gap-gap-sm mx-margin-sm w-auto Qlg:max-w-approval-card-slot-width max-w-full ${
              (entries?.[index - 1]?.type ?? "") !== "cta"
                ? "pt-padding-sm"
                : ""
            }
              ${index === longestEntrySize - 1 ? "Qlg:pb-padding-sm pb-padding-base" : ""}
              ${index === 0 ? "Qlg:pt-padding-sm pt-padding-base" : ""}
            `}
          >
            <div
              style={{
                color: entry.textColor ?? "var(--text-primary)",
              }}
              className="flex flex-col gap-gap-xs text-font-sm"
            >
              <div>
                {standardAccountNumberFormat(
                  entry.text ?? "0000000000000",
                  true,
                )}
              </div>
              <div className="text-font-xs text-text-secondary">
                {entry.subText ?? "Account number"}
              </div>
            </div>
            <div className="rounded-l-border-radius-md Qlg:rounded-r-border-radius-md rounded-r-none px-padding-sm py-padding-button-s bg-text-primary mr-[calc(var(--margin-md)*-1)] Qlg:mr-margin-base">
              <CommonBankLogo
                ifsc={entry.ifsc ?? ""}
                variant="big"
              />
            </div>
          </div>
        );
      case "amount":
        return (
          <div
            className={`flex flex-row justify-between items-center gap-gap-sm mx-margin-sm w-auto Qlg:max-w-approval-card-slot-width max-w-full ${
              (entries?.[index - 1]?.type ?? "") !== "cta"
                ? "pt-padding-sm"
                : ""
            }
              ${index === longestEntrySize - 1 ? "Qlg:pb-padding-sm pb-padding-base" : ""}
              ${index === 0 ? "Qlg:pt-padding-sm pt-padding-base" : ""}
            `}
          >
            <div className="flex flex-col gap-gap-xs">
              <div className="flex flex-row gap-gap-sm items-center">
                <div
                  className={`text-settled font-semibold text-font-sm pr-padding-sm , ${entry.shouldShowRefreshButton ? "border-primary-bg border-r" : ""}`}
                >
                  {entry.text === "xxxx.xx" ||
                  isNaN(+entry.text) ||
                  entry.text === null ||
                  entry.text === undefined
                    ? ""
                    : "INR "}{" "}
                  {FormatAmount({ price: entry.text })}
                </div>
                {entry.shouldShowRefreshButton && (
                  <div
                    className="cursor-pointer"
                    onClick={entry.refreshOnClick ?? undefined}
                    title="Refresh"
                  >
                    <SVGComponent
                      url={ICONS.RELOAD_REFRESH_WHITE}
                      height="var(--icon-sm)"
                      width="var(--icon-sm)"
                    />
                  </div>
                )}
              </div>
              <div className="text-text-secondary text-font-xs">
                {entry.subText ?? "Balance - Some time ago"}
              </div>
            </div>
          </div>
        );
      case "textSubtext":
        return (
          <div
            className={`flex flex-row justify-between items-center gap-gap-sm 
              ${halfWidth ? "flex-1 min-w-0" : "w-full"} px-padding-sm 
              ${(entries?.[index - 1]?.type ?? "") !== "cta" ? "pt-padding-sm" : ""}
              ${index === longestEntrySize - 1 ? "Qlg:pb-padding-sm pb-padding-base" : ""}
              ${index === 0 ? "Qlg:pt-padding-sm pt-padding-base" : ""}
              `}
          >
            <TitleSubtitlePair
              enableTicker
              title={entry.text}
              titleColor={entry.textColor ?? "text-primary"}
              subtitle={entry.subText}
              variant="sm"
              isBold={entry.isBold ?? false}
            />
            {entry.icon && (
              <SVGComponent
                color={entry.iconColor ?? ""}
                url={entry.icon ?? ""}
                width="var(--icon-md)"
                height={"var(--icon-md)"}
              />
            )}
          </div>
        );
      default:
        <></>;
    }
  }
  /**
   *
   * @param {"single" | "double" | "default"} cardState
   */
  function getWidthBasedOnCardState(cardState) {
    switch (cardState) {
      case "default":
        return "scale-0";
      case "single":
        return "Qlg:flex hidden w-[calc(1*(var(--std-width)/3))] scale-full-scale";
      case "double":
        return "Qlg:flex hidden w-[calc(2*(var(--std-width)/3))] scale-full-scale";
      default:
        break;
    }
  }

  const longestEntrySize = Math.max(...entries.map((x) => x.length));
  /**
   *
   * @param {Array<any>} entry
   */
  function getJustificationBasedOnEntrySize(entry) {
    if (entry.length < longestEntrySize) {
      return "justify-start";
    }
    return "justify-between";
  }

  const [btnClicked, setbtnClicked] = useState("none");

  let clickedBtn;
  if (overrideAutoLoader) {
    clickedBtn = whichBtnClicked;
  } else {
    clickedBtn = btnClicked;
  }
  return (
    <>
      <div className="lg:w-std-width relative z-z-index-base w-auto bg-primary-nav h-full  flex flex-col rounded-border-radius-md text-text-primary box-border">
        <div className="w-full bg-primary-card h-full flex Qlg:flex-row flex-col justify-between rounded-border-radius-md  relative">
          <div
            className={`z-z-index-overlay right-right-base bottom-bottom-base h-full  ${false ? "bg-primary-card " : "bg-primary-nav-act"} absolute ${getWidthBasedOnCardState(cardState)} rounded-r-border-radius-md transition-all duration-animation-medium origin-bottom-right`}
          >
            {cardState === "double" && (
              <div className="w-width-md border-r border-primary-bg my-margin-md">
                <div className="flex flex-col justify-start items-start gap-gap-sm flex-grow-0 w-full max-w-full bg-primary-nav-act px-padding-md">
                  <div className="text-font-sm text-text-primary break-words whitespace-normal w-full">
                    {overlay2Subtext ?? "Overlay 2 subtext"}
                  </div>
                  <div className="text-font-xs text-text-primary break-words">
                    {overlay2Text ?? "Overlay 2 text"}
                  </div>
                </div>
              </div>
            )}
            <div
              className={`flex flex-row justify-start items-start gap-gap-sm flex-grow-0 ${false ? "border-l border-primary-bg" : ""} ${cardState === "double" ? "w-width-md" : "w-full"} max-w-full  px-padding-md my-margin-md rounded-r-border-radius-md`}
            >
              {overlay1Icon && (
                <div className="flex-shrink-0">
                  <SVGComponent
                    url={overlay1Icon}
                    width="var(--icon-lg)"
                    height="var(--icon-lg)"
                    color={overlay1IconColor}
                  />
                </div>
              )}
              <div className="flex flex-col gap-gap-sm justify-between h-full w-full">
                <div
                  style={{
                    color: overlay1TextColor || "var(--text-primary)",
                  }}
                  className="text-text-primary font-semibold text-font-md"
                >
                  {overlay1Text ?? "Overlay 1 heading"}
                </div>
                <div
                  style={{
                    // hyphens: "auto",
                    color: overlay1SubtextColor || "var(--text-primary)",
                  }}
                  className={`text-font-sm ${null ?? "text-text-primary"}`}
                >
                  {overlay1Subtext ?? "Overlay 1 subtext"}
                </div>
                <div className="flex-grow" />
                {overlay1Cta2Text && (
                  <PrimaryButton
                    hoverOption={overlay1Cta2HoverType || "white"}
                    btnText={overlay1Cta2Text ?? "Do action 1"}
                    btnType="button"
                    enabledBgColor={overlay1Cta2Color || "var(--primary-bg)"}
                    enabledTextColor="var(--text-primary)"
                    fullWidthRequired
                    handleFunction={overlay1Cta2OnClick ?? undefined}
                    isLoading={overlay1Cta2Loading}
                    inActiveVariantNeeded={overlay1Cta2Inactive}
                    isDisabled={overlay1Cta2Disabled}
                  />
                )}
                <PrimaryButton
                  hoverOption="white"
                  btnText={overlay1CtaText ?? "Do action"}
                  btnType="button"
                  enabledBgColor={overlay1CtaColor || "var(--primary-bg)"}
                  enabledTextColor="var(--text-primary)"
                  fullWidthRequired
                  handleFunction={overlay1CtaOnClick ?? undefined}
                  isLoading={overlay1CtaLoading}
                  inActiveVariantNeeded={overlay1CtaInactive}
                  isDisabled={overlay1CtaDisabled}
                />
              </div>
            </div>
          </div>
          <div className="Qlg:w-modules-popup-width w-full flex gap-gap-sm ">
            <div
              className={`w-full px-padding-sm pt-padding-sm Qlg:pb-padding-sm pb-padding-base flex flex-col ${getJustificationBasedOnEntrySize(entries[0])} gap-gap-sm`}
            >
              {entries[0].map((entry, i) => {
                return processEntry(entry, i, entries[0]);
              })}

              <div className="px-padding-sm Qlg:px-padding-base relative Qlg:hidden self-center w-full flex justify-center items-center">
                <hr className="w-full relative Qlg:hidden border-primary-bg self-center" />
              </div>
            </div>
          </div>
          {cardState === "single" || cardState === "default" ? (
            <div className="Qlg:w-modules-popup-width w-full  flex gap-gap-sm ">
              <div
                className={`Qlg:border-l ${cardState === "default" ? "Qlg:border-r" : ""} border-r-border-base border-l-border-base border-primary-bg w-full px-padding-sm mt-margin-sm Qlg:mb-margin-sm mb-margin-base flex flex-col ${getJustificationBasedOnEntrySize(entries[1])} gap-gap-sm`}
              >
                {entries[1].map((entry, i) => {
                  return processEntry(entry, i, entries[1]);
                })}

                <div className="px-padding-sm self-center relative Qlg:hidden w-full flex justify-center items-center">
                  <hr className="w-full relative Qlg:hidden   border-primary-bg self-center" />
                </div>
              </div>
            </div>
          ) : (
            <div className="flex Qlg:invisible visible flex-col justify-start items-start gap-gap-sm flex-grow-0 w-full Qlg:max-w-[calc(var(--std-width)/3)] max-w-full bg-primary-nav-act px-padding-md py-padding-sm Qlg:border-r border-r-0 border-primary-bg Qlg:border-b-0 border-b">
              <div className="text-font-sm text-text-primary font-semibold break-words">
                {overlay2Subtext ?? "Overlay 2 subtext"}
              </div>
              <div className="text-font-xs text-text-primary break-words">
                {overlay2Text ?? "Overlay 2 text"}
              </div>
            </div>
          )}
          {cardState === "default" ? (
            <div className="Qlg:w-modules-popup-width w-full flex gap-gap-sm">
              <div className="Qlg:max-w-[var(--modules-popup-width)] max-w-full w-full flex flex-row gap-gap-sm">
                <div
                  className={`flex-1 min-w-0 flex flex-col ${getJustificationBasedOnEntrySize(entries[2])} px-padding-sm pr-padding-base pt-padding-sm Qlg:pb-padding-sm pb-padding-base gap-gap-sm`}
                >
                  {entries[2].map((entry, i) => {
                    return processEntry(entry, i, entries[2]);
                  })}
                </div>
                {cardState === "default" && (
                  <div className="flex flex-col gap-gap-xs flex-shrink-0 pr-padding-md Qlg:pt-padding-md pt-padding-sm">
                    <SecondaryRightIconSmallButton
                      btnText={ctaBtn1Text ?? "Cta button 1"}
                      btnType="button"
                      enabledBgColor="var(--primary-nav-act)"
                      icon={ICONS.ARROW_RIGHT_WHITE}
                      imgColor="var(--text-primary)"
                      hoverOption="white"
                      enabledTextColor="var(--text-primary)"
                      handleFunction={
                        clickedBtn !== "none"
                          ? undefined
                          : (ctaBtn1OnClick ?? undefined)
                      }
                      isDisabled={clickedBtn !== "none"}
                    />
                    {ctaBtn2Text && (
                      <SecondaryRightIconSmallButton
                        btnText={ctaBtn2Text ?? "Cta button 2"}
                        btnType="button"
                        enabledBgColor="var(--primary-nav-act)"
                        icon={ICONS.ARROW_RIGHT_WHITE}
                        imgColor="var(--text-primary)"
                        hoverOption="white"
                        enabledTextColor="var(--text-primary)"
                        handleFunction={
                          clickedBtn !== "none"
                            ? undefined
                            : (ctaBtn2OnClick ?? undefined)
                        }
                        isDisabled={clickedBtn !== "none"}
                      />
                    )}
                  </div>
                )}
              </div>
            </div>
          ) : (
            // 1024 / 3 = 341.33
            <div className="flex Qlg:invisible visible flex-row justify-start items-start gap-gap-sm flex-grow-0 w-full Qlg:max-w-[calc(var(--std-width)/3)] max-w-full bg-primary-nav-act Qlg:rounded-tr-border-radius-md rounded-tr-none rounded-br-border-radius-md Qlg:rounded-bl-none rounded-bl-border-radius-md px-padding-md py-padding-sm">
              {overlay1Icon && (
                <div className="flex-shrink-0">
                  <SVGComponent
                    url={overlay1Icon}
                    width="var(--icon-lg)"
                    height="var(--icon-lg)"
                    color={overlay1IconColor}
                  />
                </div>
              )}
              <div className="flex flex-col gap-gap-sm justify-between h-full w-full">
                <div
                  style={{
                    color: overlay1TextColor || "var(--text-primary)",
                  }}
                  className="text-text-primary font-semibold text-font-md"
                >
                  {overlay1Text ?? "Overlay 1 heading"}
                </div>
                <div
                  style={{
                    // hyphens: "auto",
                    color: overlay1SubtextColor || "var(--text-primary)",
                  }}
                  className="text-text-primary text-font-sm break-words"
                >
                  {overlay1Subtext ?? "Overlay 1 subtext"}
                </div>
                <div className="flex-grow" />
                {overlay1Cta2Text && (
                  <PrimaryButton
                    hoverOption={overlay1Cta2HoverType || "white"}
                    btnText={overlay1Cta2Text ?? "Do action"}
                    btnType="button"
                    enabledBgColor={overlay1Cta2Color || "var(--primary-bg)"}
                    enabledTextColor="var(--text-primary)"
                    fullWidthRequired
                    handleFunction={overlay1Cta2OnClick ?? undefined}
                    isLoading={overlay1Cta2Loading}
                    inActiveVariantNeeded={overlay1Cta2Inactive}
                    isDisabled={overlay1Cta2Disabled}
                  />
                )}
                <PrimaryButton
                  hoverOption="white"
                  btnText={overlay1CtaText ?? "Do action"}
                  btnType="button"
                  enabledBgColor={overlay1CtaColor || "var(--primary-bg)"}
                  enabledTextColor="var(--text-primary)"
                  fullWidthRequired
                  handleFunction={overlay1CtaOnClick ?? undefined}
                  isLoading={overlay1CtaLoading}
                  inActiveVariantNeeded={overlay1CtaInactive}
                  isDisabled={overlay1CtaDisabled}
                />
              </div>
            </div>
          )}
          {cardState === "default" && (
            <div className="Qlg:absolute relative Qlg:bottom-bottom-base bottom-bottom-base self-end right-right-base rounded-border-radius-md flex flex-row w-[calc(var(--std-width)/3)] justify-end">
              {clickedBtn !== "none" ? (
                <>
                  <div className="w-max-button-width-for-approval-animation flex">
                    <div
                      className={`w-width-full rounded-br-border-radius-md rounded-tl-border-radius-md flex justify-center items-center ${
                        clickedBtn === "reject"
                          ? "bg-negative-act"
                          : "bg-positive-act"
                      } py-padding-lg`}
                    >
                      <Loader
                        height="var(--icon-lg)"
                        width="var(--icon-lg)"
                      />
                    </div>
                  </div>
                </>
              ) : (
                <>
                  <PositiveNegativeButton
                    borderRadiusTopLeft
                    btnText={
                      rejectBtnText || (
                        <>
                          Reject
                          <br />
                          account
                        </>
                      )
                    }
                    icon={ICONS.REJECT_ICON_RED_WITH_WHITE_CIRCLE}
                    handleFunction={() => {
                      setbtnClicked("reject");
                      if (typeof rejectClick === "function") {
                        rejectClick();
                      }
                    }}
                    enabledBgColor="var(--negative-act)"
                  />
                  <PositiveNegativeButton
                    borderRadiusBottomRight
                    btnText={
                      approveBtnText || (
                        <>
                          Approve
                          <br />
                          account
                        </>
                      )
                    }
                    icon={ICONS.APPROVE_CIRCLE_GREEN_CHECKMARK}
                    handleFunction={() => {
                      setbtnClicked("approve");
                      if (typeof approveClick === "function") {
                        approveClick();
                      }
                    }}
                    enabledBgColor="var(--positive-act)"
                  />
                </>
              )}
            </div>
          )}
        </div>
        <CommonAlertsStrip alerts={alerts} />
      </div>
    </>
  );
}
